import React from "react";
import { Box, Chip, alpha } from "@mui/material";
import { SUCCESS, ERROR, WARNING, INFO, TEXT_SECONDARY } from "../../theme/tokens";

type Tone = "success" | "error" | "warning" | "info" | "neutral" | "secondary";

const TONE_COLOR: Record<Tone, string> = {
  success: SUCCESS,
  error: ERROR,
  warning: WARNING,
  info: INFO,
  secondary: "#7C3AED",
  neutral: TEXT_SECONDARY,
};

/**
 * Sensible default mapping of common INTALK statuses → tone. Pages can override
 * via the `tone` prop or extend the `map` prop.
 */
const DEFAULT_MAP: Record<string, Tone> = {
  // positive
  paid: "success", delivered: "success", completed: "success", active: "success",
  open: "success", sent: "success", connected: "success", answered: "success",
  available: "success", succeeded: "success", success: "success", yes: "success",
  // negative
  failed: "error", overdue: "error", missed: "error", error: "error",
  cancelled: "error", canceled: "error", revoked: "error", no_answer: "error",
  declined: "error", inactive: "error", suspended: "error", no: "error",
  // in-progress / warning
  pending: "warning", queued: "warning", sending: "warning", ringing: "warning",
  processing: "warning", trial: "warning", busy: "warning", past_due: "warning",
  draft: "warning",
  // info
  received: "info", in_progress: "info", scheduled: "info",
  // special
  voicemail: "secondary",
  closed: "neutral",
};

export interface StatusBadgeProps {
  status?: string | null;
  /** Force a tone, ignoring the auto map. */
  tone?: Tone;
  /** Extra status→tone overrides merged over the defaults. */
  map?: Record<string, Tone>;
  /** Pretty label override (defaults to a humanized status). */
  label?: React.ReactNode;
  size?: "small" | "medium";
}

const humanize = (s: string) =>
  s.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function StatusBadge({ status, tone, map, label, size = "small" }: StatusBadgeProps) {
  const key = (status ?? "").toString().toLowerCase().trim();
  const resolved: Tone = tone ?? ({ ...DEFAULT_MAP, ...(map ?? {}) }[key] ?? "neutral");
  const color = TONE_COLOR[resolved];

  return (
    <Chip
      size={size}
      label={
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
          <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
          {label ?? (status ? humanize(status) : "—")}
        </Box>
      }
      sx={{
        height: size === "small" ? 24 : 30,
        fontWeight: 700,
        fontSize: size === "small" ? 11 : 12,
        letterSpacing: "0.01em",
        color,
        bgcolor: alpha(color, 0.12),
        border: `1px solid ${alpha(color, 0.28)}`,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}
